/**
 * navHintsService.js
 *
 * Lightweight in-memory store for developer-authored navigation hint points
 * rendered in the Matterport scene. Supports the NavHintsEditor dev panel
 * (add / edit / delete) and exposes a subscribe API consumed by
 * NavigationHints for live scene rendering.
 *
 * Persistence model: in-memory only. The editor offers a "Copy JSON" action
 * so authored hints can be committed to
 *   public/config/navHintsConfig.json
 * and loaded via `loadFromConfig()` on boot.
 */

const DEFAULT_HINT = Object.freeze({
  // Hint type:
  //   'nav'  — directional navigation arrow (uses approachVector, hides when
  //            viewer is on the wrong side). Default.
  //   'poi'  — Point of Interest marker. Omni-directional, clickable. Opens a
  //            popup with an optional description when clicked.
  //   'pill' — Wayfinding pill (destination label + live compass + distance).
  //            Clicking flies the viewer to the linked tour snapshot.
  //            Requires `targetSid`.
  //   'billboard' — Static 3D road-side billboard signage. Rendered as a
  //            real plane mesh on a base stand (post / double-post /
  //            plinth) inside the Matterport scene. Fully world-anchored:
  //            does NOT rotate to face the camera, so it feels like a
  //            physical sign sitting on the ground. See navBillboardService.
  //   'directionSign' — Multi-arm direction signpost (real-world "way to X"
  //            sign). A vertical post with up to 4 directional arms, each
  //            pointing in its own world-space yaw with its own destination
  //            label. Five visual variants (A–E). Rendered as 3D meshes by
  //            navDirectionSignService.
  //   'groundArrow' — Ground-painted chevron strip (red gradient chevrons,
  //            see Direction Signages design canvas). Lies flat on the floor
  //            and pulses when the viewer comes within `highlightRadius`.
  //            World-space heading set via `forwardYaw`. Rendered as a
  //            screen-projected DOM element with CSS rotation, with the
  //            multilingual `labels` block drawn inline above the strip.
  type: 'nav',
  // Tour snapshot SID this hint points to (used by type:'pill' for live
  // compass + distance + click navigation). Optional for other types.
  targetSid: '',
  position: { x: 0, y: 0, z: 0 },
  labels: { mr: '', en: 'Hint', hi: '' },
  // Optional longer description shown in the POI popup (multi-language).
  descriptions: { mr: '', en: '', hi: '' },
  // Icon shown above the label. Can be any emoji/glyph or one of
  // NAV_ICON_PRESETS / POI_ICON_PRESETS below.
  icon: '➤',
  // Visual preset for the label pill background.
  //   'pill'   — rounded rectangle (default)
  //   'rect'   — sharp corners
  //   'circle' — circular badge
  //   'pin'    — map pin with point at bottom
  //   'none'   — transparent (text only, with shadow)
  shape: 'pill',
  // Size preset — 'sm' | 'md' | 'lg' | 'xl' (controls font-size, padding, icon size)
  size: 'md',
  showArrow: false,        // legacy — still honoured; prefer `showIcon`
  showIcon: true,          // show the icon glyph above the label
  arrowDeg: 0,             // rotation for the icon (degrees — snaps to 90° in editor)
  // ── Pill direction (type:'pill' only) ────────────────────────────────
  // World-space yaw (degrees, Matterport convention: 0 faces -Z and
  // increases toward +X) describing the absolute direction this pill's
  // arrow "edge" points to. When set, the renderer projects a probe
  // point 1 m away in this direction, screen-projects it through the
  // SDK, and uses the on-screen vector for both the arrow rotation
  // *and* the bucketed compass label. As the viewer rotates the
  // camera, the arrow swings and the label re-buckets in lock-step,
  // because the underlying world direction is fixed.
  //
  // Authored via the editor's "Aim at target POI" / "Aim at camera"
  // buttons or a 0–360° slider. When `null`, the pill falls back to
  // legacy auto-rotate-toward-`targetSid` behaviour.
  forwardYaw: null,
  // (Deprecated, retained for backwards compat with older configs.)
  directionDeg: null,
  directionAuthorYaw: null,
  // Author-chosen direction label preset key (e.g. 'turnLeft'). Stored
  // for editor convenience; the rendered label is always re-bucketed
  // from the live camera-relative angle so it stays accurate.
  directionLabelKey: '',
  // Optional author-supplied destination name override (per language).
  // When any language has a non-empty string here, it replaces the
  // auto-derived pill name (which normally mirrors the linked Target
  // POI's labels). Useful when the pill should read differently from
  // the POI itself — e.g. the POI is "Sludge tank" but the pill says
  // "To Sludge tank — turn left at the gate". Falls through
  // language → en → mr → hi at render time.
  customName: { mr: '', en: '', hi: '' },
  // Fraction of the pill badge the arrow fills (0–1). 1 = edge-to-edge,
  // 0.4 = small centered glyph. Only honoured by type:'pill'.
  iconScale: 0.85,
  textColor: '#EF7F1A',    // warm orange to match the directional-pill accent
  bgColor: '#000000',      // black — reads cleanly with orange text at partial opacity
  bgOpacity: 0.75,
  iconColor: '',           // blank → uses textColor
  fontSize: 0,             // 0 → use size preset; >0 overrides
  fontWeight: 700,
  offsetY: 1,              // extra world-space Y offset in metres (default raises pill 1m above floor)
  visible: true,
  // ── Proximity & direction gating ──────────────────────────────────────
  // Maximum distance (metres) within which the hint becomes visible.
  // Past this distance the hint hides to avoid clutter from far away.
  maxDistance: 8,
  // For type:'pill' only — minimum distance (metres) below which the
  // pill hides. Use this to make the wayfinding pill disappear once the
  // viewer has reached the destination, so it doesn't keep nagging
  // them to "go right" while they're standing next to it. 0 disables.
  hideWithin: 1.5,
  // Optional unit vector (on the floor plane) describing the direction
  // the user must be approaching from. It is the normalized (hintPos -
  // cameraPos) at author time, so when a future viewer is standing in a
  // similar relative position to the hint, dot(approach, user→hint) is
  // high and the hint shows. If null/undefined, the hint is omni-visible.
  // POI points ignore this field.
  approachVector: null,    // { x: number, z: number } | null
  approachThreshold: 0.3,  // dot-product cutoff, -1..1 (higher = narrower cone)
  // ── POI visibility cone (type:'poi' only) ────────────────────────────
  // World-space yaw (degrees, Matterport convention) describing the
  // direction the POI's "front" faces. The POI is only shown when the
  // camera is within `viewArc` degrees (total arc, centred on viewYaw)
  // of that direction. Use this to hide a POI on the front of a
  // building from someone walking around the back.
  //   • viewYaw = null  → omnidirectional (default behaviour)
  //   • viewArc = 360   → omnidirectional even with viewYaw set
  //   • viewArc = 180   → visible from the front half-space
  //   • viewArc =  90   → visible only from a 90° cone on the front
  viewYaw: null,
  viewArc: 360,
  // Fade-in start distance (linear fade from maxDistance → fadeStart).
  fadeStart: 0,            // 0 = no fade, show at full opacity within maxDistance
  // ── Optional ambient audio (POI only in current renderer) ────────────
  // `audioSrc` is any URL or data: URL. When the viewer enters a sphere
  // of radius `audioRadius` centred on the hint, the clip starts playing;
  // it pauses again when the viewer walks away.
  audioSrc: '',            // string URL / data URL ('' = no audio)
  audioRadius: 4,          // metres — proximity trigger radius
  audioVolume: 0.8,        // 0..1
  audioLoop: true,         // loop while in range
  // ── Click-to-play voiceover (POI only) ───────────────────────────────
  // When set to a Matterport tag SID (e.g. 'h00dMaDyCpp'), a small
  // speaker icon appears next to the POI label. Clicking it plays the
  // language-specific narration from
  //   /assets/audio/voiceover/<voiceoverTagId>/<lang>.mp3
  // Use this to reuse the existing per-tag voiceover library for newly
  // authored POI markers that don't have their own embedded audio.
  voiceoverTagId: '',
  // ── Auto-play voiceover on approach (POI only) ───────────────────────
  // When `autoVoiceover` is true and the POI has a `voiceoverTagId`, the
  // narration starts automatically as soon as the camera is BOTH within
  // `autoVoiceoverRadius` metres AND looking at the POI within a cone of
  // `autoVoiceoverFacingArc` degrees (total arc, centred on camera
  // forward). Walking away or turning away pauses the narration with a
  // resume window — returning within ~60 s picks up where it left off.
  // Background music ducks to ~10% during playback and ramps back up
  // when the narration ends or is paused.
  autoVoiceover: false,
  autoVoiceoverRadius: 3,    // metres (legacy; used only when triggerPoints is empty)
  autoVoiceoverFacingArc: 90, // degrees (legacy; used only when triggerPoints is empty)
  // ── Trigger points (preferred, replaces radius/arc when populated) ──
  // Explicit list of (position, cameraYawDeg) pairs. The auto-voiceover
  // fires only when the user is within ~0.5 m of one of these points
  // AND the camera yaw is within ±30° of that point's `cameraYawDeg`.
  // Captured in the editor via "📍 Capture current pose". Multiple
  // points per POI allowed — fires when ANY point matches.
  // Shape: [{ position: { x, y, z }, cameraYawDeg: number }, …]
  triggerPoints: [],
  // ── Gallery (POI only) ───────────────────────────────────────────────
  // Curated photo set surfaced in the side gallery panel when the viewer
  // is within `galleryRadius` metres of this POI. Items are uploaded via
  // the POI editor and stored as data URLs (or static asset paths).
  // Each item is shaped:
  //   { src: string, caption: { mr, en, hi } }
  // Empty array → no gallery panel for this POI. The gallery panel
  // suppresses itself during guided tours regardless.
  gallery: [],
  galleryRadius: 6, // metres — viewer must be inside this sphere for tiles to show
  // Optional explicit sweep trigger (camera focus location). When set, the
  // gallery shows ONLY while the visitor is on this sweep — no radius math.
  // Empty string falls back to radius proximity. Authored in the editor via
  // "📍 Use current sweep".
  gallerySweepId: '',
  // ── Billboard fields (type:'billboard' only) ────────────────────────
  // World-space yaw (degrees, Matterport convention) the billboard's
  // front face points to. Authored once; the billboard never rotates
  // again — that is the entire point of this type.
  rotationY: 0,
  // Overall scale multiplier applied to the board + stand. 1.0 ≈ a
  // standard 1.6 m × 1.0 m board on a 1.4 m post (real-world sized).
  scale: 1.0,
  // Top board outline — controls how the canvas is drawn:
  //   'rect'    — sharp rectangle
  //   'rounded' — rounded corners
  //   'peaked'  — house-style with a triangular cap on top
  //   'oval'    — ellipse
  boardShape: 'rect',
  // Base stand under the board:
  //   'singlePost' — single round post (default)
  //   'doublePost' — two posts at the board edges
  //   'plinth'     — solid rectangular plinth (info-board style)
  //   'none'       — float the board with no stand
  baseStand: 'singlePost',
  // Visual styling for the canvas-painted board face. These override
  // the global poi/pill style block — billboards intentionally don't
  // share style because each one is usually unique signage.
  boardColor: '#FFFFFF',
  boardTextColor: '#111111',
  boardBorderColor: '#111111',
  // Pole/plinth colour (defaults to a metallic grey).
  standColor: '#7d7d7d',
  // ── Direction-sign fields (type:'directionSign' only) ────────────────
  // Visual variant key (A–E). Drives both the post styling (cap, colour,
  // post radius) and the arm styling (background, text colour, tip shape).
  // Render-time geometry is built by navDirectionSignService from this token.
  signVariant: 'A',
  // List of directional arms attached to the post. Each arm is an object:
  //   { labels: { mr, en, hi }, yaw: number }
  // `yaw` is world-space yaw (degrees) the arm points to. Up to 4 arms
  // recommended; the renderer stacks them down the post.
  signArms: [],
  // Optional per-hint overrides on top of the variant palette. When
  // unset, the variant's default colour for that part is used. Hex
  // strings ('#RRGGBB') only.
  //   • signFill        — arm plaque background
  //   • signTextColor   — arm label text
  //   • signBorderColor — double-line border on the arm (variant F)
  //   • signAccentColor — engraved arrow / decorative accent
  //   • signPostColor   — post (pole) main colour
  //   • signBrassColor  — finial cap, mounting clamp, bolts (brass parts)
  //   • signFontScale   — multiplier on the canvas font size (0.6–1.4)
  signFill: '',
  signTextColor: '',
  signBorderColor: '',
  signAccentColor: '',
  signPostColor: '',
  signBrassColor: '',
  signFontScale: 1.0,
  // ── Ground arrow fields (type:'groundArrow' only) ────────────────────
  // Length of the chevron strip in metres. The strip is drawn from the
  // anchor point forward (along `forwardYaw`) for this many metres.
  arrowLength: 2.5,
  // Width of the chevron strip in metres.
  arrowWidth: 0.9,
  // Total bend across the strip in degrees. 0 = straight (default).
  // Positive values curve the strip to the RIGHT (CW from above,
  // matching Matterport yaw convention); negative values curve LEFT.
  // Use ±60–120° to signage a turn at a corner.
  turnAngle: 0,
  // Distance (m) below which the arrow plays its highlight pulse animation.
  // Encourages the visitor that they're "on the path".
  highlightRadius: 3,
  // Tint applied to the chevrons. Built-in tail→head gradient ramps
  // from `arrowTailOpacity` at the tail to 1.0 at the head, all in
  // this colour. Default: brand saffron.
  arrowColor: '#f59e0b',
  // Visual variant for the chevron strip:
  //   'chevrons'       — classic 7-chevron strip with V-notched backs
  //   'triangles'      — solid triangular arrowheads (no V-notch)
  //   'doubleChevron'  — fewer, thicker chevrons with a wider gap
  //   'dashes'         — simple dashed rectangles (no arrowhead shape)
  //   'beam'           — single solid tapered beam (one big arrow)
  arrowVariant: 'chevrons',
  // Opacity of the tail end of the strip (0..1). The head end is always
  // fully opaque; the gradient ramps linearly between the two.
  arrowTailOpacity: 0.05,
  // Tail-to-head flow animation. When true, a subtle pulse drifts
  // along the strip from tail to head, hinting at the direction of
  // travel without grabbing attention. The static tail→head gradient
  // remains and the pulse rides on top of it.
  arrowFlow: true,
  // Speed of the flow pulse in cycles per second (one full traversal
  // tail → head per cycle). 0.25 ≈ ~4 seconds per traversal — a slow,
  // calm drift.
  arrowFlowSpeed: 0.25,
  // Width of the flow pulse as a fraction of the strip length (0..1).
  // Wider value → softer wash, less obvious "running light".
  arrowFlowWidth: 0.35,
  // Minimum opacity outside the pulse (0..1). Kept high so the flow
  // is a gentle shimmer, not a flicker.
  arrowFlowFloor: 0.8,
  // Vertical height (metres) of the floating label above the arrow head.
  // Larger = label sits higher off the ground.
  labelHeight: 0.6,
  // Font size (px) of the floating label. The label is screen-aligned
  // so this is a pixel size, not metres.
  labelFontSize: 16,
  // Per-hint label wrapping mode for ground arrows:
  //   'auto' — single line, no wrap (default; matches old behaviour).
  //   '1'    — explicit single line (identical to auto for arrows).
  //   '2'    — split the label onto two lines. The renderer uses any
  //            literal '\n' the author entered, otherwise it breaks at
  //            the most balanced whitespace position.
  labelLineMode: 'auto',
  // World-space scale applied to the label sign quad (metres per CSS
  // pixel). The label is drawn on a fixed vertical plane in the world
  // (it foreshortens with camera angle instead of billboarding), and
  // this knob controls how big it appears at 1 m viewing distance.
  // 0.01 means a 16px font renders as a ~16 cm-tall sign in world.
  labelWorldScale: 0.01,

});

// Arrow / directional icons — for navigation hints.
export const NAV_ICON_PRESETS = [
  { value: '➤', label: 'Arrow ➤' },
  { value: '➡', label: 'Arrow ➡' },
  { value: '➜', label: 'Arrow ➜' },
  { value: '↑', label: 'Up ↑' },
  { value: '↓', label: 'Down ↓' },
  { value: '←', label: 'Left ←' },
  { value: '→', label: 'Right →' },
  { value: '↗', label: 'Up-right ↗' },
  { value: '↙', label: 'Down-left ↙' },
  { value: '⬆', label: 'Chevron Up ⬆' },
  { value: '⬇', label: 'Chevron Down ⬇' },
  { value: '⤴', label: 'Turn up ⤴' },
  { value: '⤵', label: 'Turn down ⤵' },
  { value: '⇧', label: 'Up block ⇧' },
  { value: '', label: '(none)' },
];

// Mandir / POI themed icons — for point-of-interest markers.
export const POI_ICON_PRESETS = [
  { value: '🕉', label: 'Om 🕉' },
  { value: '🛕', label: 'Temple 🛕' },
  { value: '🔔', label: 'Bell 🔔' },
  { value: '🪔', label: 'Diya 🪔' },
  { value: '🌸', label: 'Flower 🌸' },
  { value: '🙏', label: 'Pranam 🙏' },
  { value: '📿', label: 'Mala 📿' },
  { value: '🪷', label: 'Lotus 🪷' },
  { value: '🕯', label: 'Candle 🕯' },
  { value: '📍', label: 'Pin 📍' },
  { value: 'ℹ', label: 'Info ℹ' },
  { value: '★', label: 'Star ★' },
  { value: '●', label: 'Dot ●' },
  { value: '◆', label: 'Diamond ◆' },
  { value: '📷', label: 'Camera 📷' },
  { value: '🎶', label: 'Music 🎶' },
  { value: '', label: '(none)' },
];

// Kept for backwards compatibility — older imports used ICON_PRESETS.
export const ICON_PRESETS = NAV_ICON_PRESETS;

// ── Wayfinding pill direction-label presets ────────────────────────────
// Authored direction labels for type:'pill' hints. `key` is persisted in
// the hint and resolved by NavigationHints into a localised string at
// render time. `arrowDeg` is the suggested arrow rotation that pairs
// with the label — the editor uses it to auto-fill the manual rotation
// when the author picks a preset (the author can still override).
export const PILL_DIRECTION_LABEL_KEYS = [
  { key: '',           label: 'Auto (from target)', arrowDeg: null },
  { key: 'straight',   label: 'Straight',           arrowDeg: 0    },
  { key: 'slightLeft', label: 'Slight left',        arrowDeg: -45  },
  { key: 'left',       label: 'Left',               arrowDeg: -90  },
  { key: 'turnLeft',   label: 'Turn left',          arrowDeg: -90  },
  { key: 'uTurn',      label: 'U-turn',             arrowDeg: 180  },
  { key: 'turnRight',  label: 'Turn right',         arrowDeg: 90   },
  { key: 'right',      label: 'Right',              arrowDeg: 90   },
  { key: 'slightRight',label: 'Slight right',       arrowDeg: 45   },
  { key: 'behind',     label: 'Behind',             arrowDeg: 180  },
];

export const SHAPE_PRESETS = [
  { value: 'pill', label: 'Pill (rounded)' },
  { value: 'rect', label: 'Rectangle (sharp)' },
  { value: 'circle', label: 'Circle badge' },
  { value: 'pin', label: 'Map pin' },
  { value: 'none', label: 'Text only' },
];

// ── Billboard-specific presets (for type:'billboard') ──────────────────
// Surfaced to the NavHintsEditor so authors can pick the board outline
// and the kind of stand it sits on. Render-time geometry is built by
// navBillboardService from these tokens.
export const BILLBOARD_BOARD_SHAPES = [
  { value: 'rect',    label: 'Rectangle' },
  { value: 'rounded', label: 'Rounded rect' },
  { value: 'peaked',  label: 'Peaked top' },
  { value: 'oval',    label: 'Oval' },
];

export const BILLBOARD_BASE_STANDS = [
  { value: 'singlePost', label: 'Single post' },
  { value: 'doublePost', label: 'Double post' },
  { value: 'plinth',     label: 'Plinth' },
  { value: 'none',       label: 'No stand (float)' },
];

// ── Direction-sign visual variants (for type:'directionSign') ──────────
// Five hand-tuned visual styles ported from the Direction Signages design
// canvas. Each token resolves at render time (in navDirectionSignService)
// to a full styling object: post colour, cap, arm bg, text colour, etc.
export const DIRECTION_SIGN_VARIANTS = [
  { value: 'A', label: 'A · Saffron arrow / charcoal post' },
  { value: 'B', label: 'B · Cream panel / brass kalash cap' },
  { value: 'C', label: 'C · Modular monolith / chevron tip' },
  { value: 'D', label: 'D · Cream slab / saffron edge' },
  { value: 'E', label: 'E · Bronze on sandstone (temple)' },
  { value: 'F', label: 'F · Brass-banded metal / ivory plaques' },
];

export const SIZE_PRESETS = [
  { value: 'sm', label: 'Small',  fontSize: 13, iconSize: 20, padX: 8,  padY: 3 },
  { value: 'md', label: 'Medium', fontSize: 14, iconSize: 28, padX: 12, padY: 5 },
  { value: 'lg', label: 'Large',  fontSize: 22, iconSize: 38, padX: 16, padY: 7 },
  { value: 'xl', label: 'XL',     fontSize: 30, iconSize: 52, padX: 22, padY: 10 },
];

const SIZE_MAP = Object.fromEntries(SIZE_PRESETS.map((s) => [s.value, s]));
export const getSizePreset = (v) => SIZE_MAP[v] || SIZE_MAP.md;

// ── Global per-type style ──────────────────────────────────────────────
// All visual knobs for POI / Nav pill live here rather than per-hint, so
// a single change applies uniformly across the whole tour. Authors only
// pick labels / position / target-POI per hint. Persisted alongside the
// hint list in /public/config/navHintsConfig.json under `globalStyle`.
const DEFAULT_GLOBAL_STYLE = Object.freeze({
  poi: {
    shape: 'pill',
    size: 'md',
    fontSize: 14,
    textColor: '#FFFFFF',
    bgColor: '#000000',
    bgOpacity: 0.75,
    fontWeight: 700,
    offsetY: 0,
  },
  pill: {
    shape: 'pill',
    size: 'md',
    fontSize: 14,
    textColor: '#FFFFFF',
    bgColor: '#0A0A0C',
    bgOpacity: 0.82,
    fontWeight: 700,
    offsetY: 0,
    // Fraction of the pill badge filled by the arrow glyph (0–1).
    iconScale: 1,
  },
  // Ground-arrow style is fully global so the whole tour shares one
  // visual language. Per-hint fields that DO vary by location stay
  // per-hint: position, forwardYaw (heading), turnAngle, arrowLength,
  // labels, labelHeight, audio. Everything else below comes from here.
  groundArrow: {
    arrowVariant: 'chevrons',
    arrowColor: '#f59e0b',
    arrowWidth: 0.9,
    arrowTailOpacity: 0.05,
    // Override chevron count. 0 = follow the variant's natural count
    // (chevrons:7, triangles:6, doubleChevron:4, dashes:9, beam:1).
    // Any positive integer (1–20) overrides it so authors can add
    // more breaks (more chevrons) or fewer.
    chevronCount: 0,
    highlightRadius: 3,
    arrowFlow: true,
    arrowFlowSpeed: 0.25,
    arrowFlowWidth: 0.35,
    arrowFlowFloor: 0.8,
    labelFontSize: 16,
    labelWorldScale: 0.01,
    // Default vertical offset of the label above the arrow head, in
    // metres. Per-hint `labelHeight` overrides this for individual
    // arrows (e.g. when a wall or fixture is in the way).
    labelHeight: 0.6,
  },
  // Direction-sign visibility is fully global — the whole tour shares
  // one visibility envelope. Position / yaw / arms / colours stay
  // per-hint; only the proximity gate lives here.
  directionSign: {
    // Maximum distance (metres on the floor plane) within which the
    // signpost is shown. Past this distance the 3D meshes are hidden
    // (group.visible = false) so distant signs don't clutter the view.
    maxDistance: 25,
    // Linear fade-in start distance. 0 = no fade (hard cutoff).
    // When > 0 and < maxDistance, the sign opacity ramps from 0 at
    // maxDistance → 1 at fadeStart.
    fadeStart: 0,
    // Hide when the camera is closer than this (metres). Prevents
    // the brass cap / band / clamp materials from clipping at the
    // near plane and painting thin yellow streaks across the screen
    // when the visitor stands right next to (or on top of) the post.
    // 0 disables.
    hideWithin: 0.8,
  },
});
let globalStyle = {
  poi: { ...DEFAULT_GLOBAL_STYLE.poi },
  pill: { ...DEFAULT_GLOBAL_STYLE.pill },
  groundArrow: { ...DEFAULT_GLOBAL_STYLE.groundArrow },
  directionSign: { ...DEFAULT_GLOBAL_STYLE.directionSign },
};

let nextId = 1;
const hints = new Map(); // id -> hint object
const listeners = new Set();

const newId = () => `nh_${Date.now().toString(36)}_${nextId++}`;
const notify = () => listeners.forEach((fn) => { try { fn(); } catch (_) {} });

const navHintsService = {
  /** Subscribe for changes. Returns unsubscribe fn. */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** Get all hints as an array (stable order by insertion). */
  getAll() {
    return Array.from(hints.values());
  },

  getById(id) {
    return hints.get(id) || null;
  },

  /**
   * Add a hint. `partial` is merged over DEFAULT_HINT. Returns assigned id.
   */
  add(partial = {}) {
    const id = partial.id || newId();
    const hint = {
      ...DEFAULT_HINT,
      ...partial,
      id,
      position: { ...DEFAULT_HINT.position, ...(partial.position || {}) },
      labels: { ...DEFAULT_HINT.labels, ...(partial.labels || {}) },
      descriptions: { ...DEFAULT_HINT.descriptions, ...(partial.descriptions || {}) },
      customName: { ...DEFAULT_HINT.customName, ...(partial.customName || {}) },
    };
    // Type-aware vertical offset default: POIs sit at their anchor (0m),
    // pills & nav arrows float 1m above the floor, ground-arrows lie
    // flush on the floor (2 cm float to avoid z-fighting). Only applied
    // when the caller did not specify an explicit offsetY.
    if (partial.offsetY == null) {
      hint.offsetY = hint.type === 'poi' ? 0
        : hint.type === 'groundArrow' ? 0.02
        : 1;
    }
    hints.set(id, hint);
    notify();
    return id;
  },

  /** Shallow-merge patch over existing hint. Deep-merges position and labels. */
  update(id, patch) {
    const existing = hints.get(id);
    if (!existing) return false;
    const next = {
      ...existing,
      ...patch,
      position: { ...existing.position, ...(patch.position || {}) },
      labels: { ...existing.labels, ...(patch.labels || {}) },
      descriptions: { ...(existing.descriptions || {}), ...(patch.descriptions || {}) },
      customName: { ...(existing.customName || DEFAULT_HINT.customName), ...(patch.customName || {}) },
    };
    hints.set(id, next);
    notify();
    return true;
  },

  remove(id) {
    const ok = hints.delete(id);
    if (ok) notify();
    return ok;
  },

  clear() {
    hints.clear();
    notify();
  },

  /**
   * Load an array of hints from a config file (e.g. navHintsConfig.json).
   * Replaces current state.
   */
  replaceAll(list = []) {
    hints.clear();
    for (const h of list) {
      const id = h.id || newId();
      const merged = {
        ...DEFAULT_HINT,
        ...h,
        id,
        position: { ...DEFAULT_HINT.position, ...(h.position || {}) },
        labels: { ...DEFAULT_HINT.labels, ...(h.labels || {}) },
        descriptions: { ...DEFAULT_HINT.descriptions, ...(h.descriptions || {}) },
        customName: { ...DEFAULT_HINT.customName, ...(h.customName || {}) },
      };
      // Apply type-aware offsetY default when the hint in the config
      // does not pin one explicitly. (POI → 0m, pill/nav → 1m,
      // groundArrow → 0.02m.)
      if (h.offsetY == null) {
        merged.offsetY = merged.type === 'poi' ? 0
          : merged.type === 'groundArrow' ? 0.02
          : 1;
      }
      // Migrate legacy direction format: older configs stored the
      // direction as a camera-relative offset + captured yaw. Combine
      // them into the new world-space `forwardYaw` so the renderer has
      // a single source of truth.
      if (
        merged.forwardYaw == null
        && merged.directionAuthorYaw != null
        && merged.directionDeg != null
      ) {
        let y = merged.directionAuthorYaw + merged.directionDeg;
        y = ((y % 360) + 360) % 360;
        merged.forwardYaw = parseFloat(y.toFixed(2));
      }
      hints.set(id, merged);
    }
    notify();
  },

  /** Export current hints as a plain JS array suitable for JSON.stringify. */
  toJSON() {
    return {
      globalStyle: {
        poi: { ...globalStyle.poi },
        pill: { ...globalStyle.pill },
        groundArrow: { ...globalStyle.groundArrow },
        directionSign: { ...globalStyle.directionSign },
      },
      hints: this.getAll().map((h) => ({
        id: h.id,
        type: h.type || 'nav',
        targetSid: h.targetSid || '',
        position: h.position,
        labels: h.labels,
        descriptions: h.descriptions,
        icon: h.icon,
        arrowDeg: h.arrowDeg,
        forwardYaw: h.forwardYaw ?? null,
        directionDeg: h.directionDeg ?? null,
        directionAuthorYaw: h.directionAuthorYaw ?? null,
        directionLabelKey: h.directionLabelKey || '',
        customName: h.customName || { mr: '', en: '', hi: '' },
        maxDistance: h.maxDistance,
        hideWithin: h.hideWithin ?? 0,
        approachVector: h.approachVector,
        approachThreshold: h.approachThreshold,
        viewYaw: h.viewYaw ?? null,
        viewArc: h.viewArc ?? 360,
        fadeStart: h.fadeStart,
        offsetY: h.offsetY,
        audioSrc: h.audioSrc,
        audioRadius: h.audioRadius,
        audioVolume: h.audioVolume,
        audioLoop: h.audioLoop,
        voiceoverTagId: h.voiceoverTagId || '',
        autoVoiceover: !!h.autoVoiceover,
        autoVoiceoverRadius: h.autoVoiceoverRadius ?? 3,
        autoVoiceoverFacingArc: h.autoVoiceoverFacingArc ?? 90,
        triggerPoints: Array.isArray(h.triggerPoints) ? h.triggerPoints.map((p) => ({
          position: {
            x: typeof p?.position?.x === 'number' ? p.position.x : 0,
            y: typeof p?.position?.y === 'number' ? p.position.y : 0,
            z: typeof p?.position?.z === 'number' ? p.position.z : 0,
          },
          cameraYawDeg: typeof p?.cameraYawDeg === 'number' ? p.cameraYawDeg : 0,
        })) : [],
        // Billboard-only fields. Always written so editors picking up
        // older configs see explicit defaults rather than `undefined`.
        rotationY: h.rotationY ?? 0,
        scale: h.scale ?? 1.0,
        boardShape: h.boardShape || 'rect',
        baseStand: h.baseStand || 'singlePost',
        boardColor: h.boardColor || '#FFFFFF',
        boardTextColor: h.boardTextColor || '#111111',
        boardBorderColor: h.boardBorderColor || '#111111',
        standColor: h.standColor || '#7d7d7d',
        // Direction-sign-only fields. Always written so older configs see
        // explicit defaults rather than `undefined`.
        signVariant: h.signVariant || 'A',
        signArms: Array.isArray(h.signArms) ? h.signArms.map((a) => {
          const out = {
            labels: {
              mr: a?.labels?.mr || '',
              en: a?.labels?.en || '',
              hi: a?.labels?.hi || '',
            },
            yaw: typeof a?.yaw === 'number' ? a.yaw : 0,
          };
          // Persist the arm's POI link and the override flag so the
          // editor's "Target POI" + "custom labels" choices survive a
          // save/reload cycle. Older configs simply lack these keys
          // and remain backward-compatible (treated as no link).
          if (a?.targetId) out.targetId = a.targetId;
          if (a?.customLabels === true) out.customLabels = true;
          // Per-arm layout overrides set by the editor:
          //   • lineMode: 'auto' (default — omit), '1' force single
          //     line, '2' force two-line wrap.
          //   • fontSizePx: explicit pixel size that overrides the
          //     auto-fit (and the shared min-across-arms) for this
          //     arm only. Omitted when 0/unset.
          if (a?.lineMode === '1' || a?.lineMode === '2') out.lineMode = a.lineMode;
          if (Number.isFinite(a?.fontSizePx) && a.fontSizePx > 0) {
            out.fontSizePx = Math.round(a.fontSizePx);
          }
          return out;
        }) : [],
        // Ground-arrow-only fields. Always written so older configs see
        // explicit defaults rather than `undefined`.
        arrowLength: h.arrowLength ?? 2.5,
        arrowWidth: h.arrowWidth ?? 0.9,
        turnAngle: h.turnAngle ?? 0,
        highlightRadius: h.highlightRadius ?? 3,
        arrowColor: h.arrowColor || '#f59e0b',
        arrowVariant: h.arrowVariant || 'chevrons',
        arrowTailOpacity: h.arrowTailOpacity ?? 0.05,
        labelHeight: h.labelHeight ?? 0.6,
        labelFontSize: h.labelFontSize ?? 16,
        labelLineMode: h.labelLineMode || 'auto',
        // Ground arrow's linked Target POI + override flag. Without
        // these the editor's "Target POI" pick is lost on save/reload
        // (the arrow falls back to its own labels which are usually
        // empty), making the on-map label disappear.
        ...(h.targetId ? { targetId: h.targetId } : {}),
        ...(h.customLabels === true ? { customLabels: true } : {}),
        // POI-only: side-gallery photo set + visibility radius / sweep
        // gating. Must be persisted explicitly — the editor lets
        // authors curate these per POI and they were silently dropped
        // on save before this was added.
        gallery: Array.isArray(h.gallery) ? h.gallery.slice() : [],
        galleryRadius: h.galleryRadius ?? 6,
        gallerySweepId: h.gallerySweepId || '',
      })),
    };
  },

  /** ── Global per-type style accessors ─────────────────────────────── */
  getGlobalStyle(type) {
    const t = type === 'pill' ? 'pill'
      : type === 'groundArrow' ? 'groundArrow'
      : type === 'directionSign' ? 'directionSign'
      : 'poi';
    return { ...DEFAULT_GLOBAL_STYLE[t], ...globalStyle[t] };
  },
  updateGlobalStyle(type, patch) {
    const t = type === 'pill' ? 'pill'
      : type === 'groundArrow' ? 'groundArrow'
      : type === 'directionSign' ? 'directionSign'
      : 'poi';
    globalStyle[t] = { ...globalStyle[t], ...(patch || {}) };
    notify();
  },
  replaceGlobalStyle(next = {}) {
    globalStyle = {
      poi: { ...DEFAULT_GLOBAL_STYLE.poi, ...(next.poi || {}) },
      pill: { ...DEFAULT_GLOBAL_STYLE.pill, ...(next.pill || {}) },
      groundArrow: { ...DEFAULT_GLOBAL_STYLE.groundArrow, ...(next.groundArrow || {}) },
      directionSign: { ...DEFAULT_GLOBAL_STYLE.directionSign, ...(next.directionSign || {}) },
    };
    notify();
  },

  /**
   * Fetch the static config file shipped in /public/config and replace
   * current state. Safe to call on boot — silently no-ops on 404.
   */
  async loadFromConfig(path = '/config/navHintsConfig.json') {
    try {
      const res = await fetch(path, { cache: 'no-store' });
      if (!res.ok) return false;
      const data = await res.json();
      if (Array.isArray(data)) {
        this.replaceAll(data);
      } else {
        if (Array.isArray(data?.hints)) this.replaceAll(data.hints);
        if (data?.globalStyle) this.replaceGlobalStyle(data.globalStyle);
      }
      return true;
    } catch (_) {
      return false;
    }
  },

  // ── Editor-active pubsub ───────────────────────────────────────────────
  // NavigationHints watches this flag so it can render developer-only
  // overlays (e.g. maxDistance/audioRadius visualisation rings) without
  // leaking into the live viewer experience.
  _editorActive: false,
  _editorListeners: new Set(),
  isEditorActive() { return !!this._editorActive; },
  setEditorActive(v) {
    const next = !!v;
    if (next === this._editorActive) return;
    this._editorActive = next;
    this._editorListeners.forEach((fn) => { try { fn(next); } catch (_) {} });
  },
  subscribeEditorActive(fn) {
    this._editorListeners.add(fn);
    return () => this._editorListeners.delete(fn);
  },

  // ── Editor selection pubsub ────────────────────────────────────────────
  // Tracks which hint is currently being edited so the renderer can show
  // the influence-radius ring only for that hint.
  _selectedId: null,
  _selectionListeners: new Set(),
  getEditorSelectedId() { return this._selectedId; },
  setEditorSelectedId(id) {
    const next = id || null;
    if (next === this._selectedId) return;
    this._selectedId = next;
    this._selectionListeners.forEach((fn) => { try { fn(next); } catch (_) {} });
  },
  subscribeEditorSelection(fn) {
    this._selectionListeners.add(fn);
    return () => this._selectionListeners.delete(fn);
  },
};

export default navHintsService;
export { DEFAULT_HINT };

// Auto-load the static config on first import. Runs once in the background.
if (typeof window !== 'undefined') {
  navHintsService.loadFromConfig();
}
